// Libs
import React, { useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
// Actions
import { mutate1Object as mutate1ObjectInCore } from 'core/actions';
// Selectors
import { selectDisplaySort, selectDatasourceFields, selectDatasourceData } from 'core/selectors';
import { selectProfile } from 'screens/Login/selectors';
// Components
import { Wrapper, Dropdown, Caption } from 'components';
import Menu from 'components/Menu';
import { Span } from 'components';
import { SortIcon } from 'components/Icons';

const LABEL_PREFIX = '__label__';

const FIELD_CATEGORIES = [
	{ label: 'Tiempo', ids: ['executionAt', 'dilation', 'range', 'orderTemporizator'] },
	{ label: 'Cliente', ids: ['clientIdc', 'clientName', 'cityName', 'townshipName', 'zoneId'] },
	{ label: 'Asignación', ids: ['serviceId', 'assignedTechId', 'entityName'] },
	{ label: 'Estado', ids: ['odtId', 'eventId', 'stateName', 'price'] },
];

const CategoryLabel = styled.div`
	padding: 8px 16px 2px;
	font-size: 10px;
	letter-spacing: 0.08em;
	text-transform: uppercase;
	color: ${({ theme }) => theme.fonts.colors.emphasis_1};
	border-top: 1px solid rgba(255, 255, 255, 0.06);
	margin-top: 2px;
`;

const Header = styled.div`
	padding: 6px 16px 8px;
	border-bottom: 1px solid rgba(255, 255, 255, 0.06);
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
`;

const DirectionToggle = styled.div`
	display: flex;
	gap: 3px;
	flex-shrink: 0;
`;

const DirButton = styled.button`
	padding: 1px 8px;
	font-size: 10px;
	border-radius: 3px;
	border: 1px solid ${({ $active }) => $active ? '#1EB980' : 'rgba(255,255,255,0.2)'};
	background: ${({ $active }) => $active ? 'rgba(30,185,128,0.15)' : 'transparent'};
	color: ${({ $active }) => $active ? '#1EB980' : 'rgba(255,255,255,0.45)'};
	cursor: pointer;
	transition: all 0.15s;
	&:hover {
		border-color: #1EB980;
		color: #1EB980;
	}
`;

const ColorDot = styled.span`
	display: inline-block;
	width: 10px;
	height: 10px;
	border-radius: 2px;
	background-color: ${({ color }) => color};
	margin-right: 8px;
	flex-shrink: 0;
`;

const itemStyle = { padding: '2px 16px', height: 'auto', lineHeight: '26px' };

function getSchemeLabels(scheme) {
	if (!scheme) return [];
	const labels = [];
	const add = (key, item) => {
		if (item?.style?.backgroundColor && item?.label)
			labels.push({ key: `${LABEL_PREFIX}${item.style.backgroundColor}`, label: item.label, color: item.style.backgroundColor, order: item.order ?? 99 });
	};
	add('warranty', scheme.warranty);
	add('inconsistency', scheme.inconsistency);
	add('priority_one', scheme.priority_one);
	add('priority_two', scheme.priority_two);
	add('priority_three', scheme.priority_three);
	add('tracked', scheme.tracked);
	add('reprogram_next', scheme.reprogram?.next_day);
	add('reprogram_normal', scheme.reprogram?.normal);
	return labels.sort((a, b) => a.order - b.order);
}

const SortPicker = () => {
	const dispatch = useDispatch();
	const sort = useSelector(selectDisplaySort);
	const fields = useSelector(selectDatasourceFields);
	const profile = useSelector(selectProfile);
	const datasource = useSelector(selectDatasourceData);
	const [open, setOpen] = useState(false);

	// Keep a ref always pointing to latest sort so callbacks never go stale
	const sortRef = useRef(sort);
	sortRef.current = sort;

	const sortableFields = React.useMemo(
		() => fields.filter(f => f.id !== 'index'),
		[fields],
	);
	const isSortPickerAvailable = sortableFields.length > 0;

	const usedColors = React.useMemo(
		() => new Set(datasource.map(row => row.priority?.style?.backgroundColor).filter(Boolean)),
		[datasource],
	);

	const populatedFields = React.useMemo(
		() => new Set(
			sortableFields
				.filter(f => datasource.some(row => row[f.id] != null && row[f.id] !== ''))
				.map(f => f.id),
		),
		[datasource, sortableFields],
	);
	const scheme = profile?.organization?.order_settings?.tracking?.scheme;
	const schemeLabels = getSchemeLabels(scheme).filter(lbl =>
		usedColors.has(lbl.color),
	);
	const hasPrioritySort = schemeLabels.length > 0;

	const onSelect = useCallback(({ key }) =>
		dispatch(mutate1ObjectInCore('display', { sort: { dataKey: key, direction: sortRef.current.direction || 'asc' } })),
		[dispatch],
	);

	const onDirection = useCallback((e, dir) => {
		e.stopPropagation();
		dispatch(mutate1ObjectInCore('display', { sort: { dataKey: sortRef.current.dataKey, direction: dir } }));
	}, [dispatch]);

	React.useEffect(() => {
		if (isSortPickerAvailable && hasPrioritySort && !sort.dataKey) {
			dispatch(
				mutate1ObjectInCore('display', {
					sort: { dataKey: '__priority__', direction: sort.direction || 'asc' },
				}),
			);
		}
	}, [dispatch, hasPrioritySort, isSortPickerAvailable, sort.dataKey, sort.direction]);

	if (!isSortPickerAvailable) return null;

	const activeSortableFields = sortableFields.filter(f => populatedFields.has(f.id));

	const getFieldGroup = f => {
		if (f.group) return f.group;
		for (const cat of FIELD_CATEGORIES)
			if (cat.ids.includes(f.id)) return cat.label;
		return null;
	};

	const groupMap = new Map();
	const uncategorized = [];
	activeSortableFields.forEach(f => {
		const group = getFieldGroup(f);
		if (group) {
			if (!groupMap.has(group)) groupMap.set(group, []);
			groupMap.get(group).push(f);
		} else {
			uncategorized.push(f);
		}
	});
	const sections = [...groupMap.entries()].map(([label, fields]) => ({ label, fields }));

	const renderField = field => (
		<Menu.Item key={field.id} style={itemStyle}>
			<Span selected={sort.dataKey === field.id}>{field.name}</Span>
		</Menu.Item>
	);

	const overlay = (
		<Menu.Wrapper mode='inline' onClick={onSelect} style={{ minWidth: 200 }}>
			<Header>
				<Caption emphasis>Ordenar por</Caption>
				<DirectionToggle>
					<DirButton $active={sort.direction !== 'desc'} onClick={e => onDirection(e, 'asc')}>Asc</DirButton>
					<DirButton $active={sort.direction === 'desc'} onClick={e => onDirection(e, 'desc')}>Desc</DirButton>
				</DirectionToggle>
			</Header>

			{/* Etiquetas */}
			{schemeLabels.length > 0 && (
				<>
					<CategoryLabel>Etiquetas</CategoryLabel>
					{hasPrioritySort && (
						<Menu.Item key='__priority__' style={itemStyle}>
							<Span selected={sort.dataKey === '__priority__'}>Por etiquetas</Span>
						</Menu.Item>
					)}
					{schemeLabels.map(lbl => (
						<Menu.Item key={lbl.key} style={itemStyle}>
							<Wrapper padding='0' alignItems='center'>
								<ColorDot color={lbl.color} />
								<Span selected={sort.dataKey === lbl.key}>{lbl.label}</Span>
							</Wrapper>
						</Menu.Item>
					))}
				</>
			)}

			{/* Uncategorized */}
			{uncategorized.map(renderField)}

			{/* Field sections */}
			{sections.map(section => (
				<React.Fragment key={section.label}>
					<CategoryLabel>{section.label}</CategoryLabel>
					{section.fields.map(renderField)}
				</React.Fragment>
			))}
		</Menu.Wrapper>
	);

	return (
		<Wrapper padding='0'>
			<Dropdown.Wrapper
				trigger={['click']}
				overlay={() => overlay}
				visible={open}
				onVisibleChange={setOpen}
			>
				<SortIcon button selected={!!sort.dataKey} />
			</Dropdown.Wrapper>
		</Wrapper>
	);
};

export default SortPicker;
